import type { Countdown } from './storage';
import { dateToLocalISO } from './format';

/// /to?... share URL the Cloud Function's appV2.get('/to') handler parses
/// (functions/index.js). Same query shape the iOS app emits, so a PWA-shared
/// countdown lands on the same server-rendered page with the same OG preview
/// and "Save to my countdowns" import button.
export function buildShareURL(c: Countdown, origin: string = location.origin): string {
  return `${origin}/to?${buildCountdownParams(c)}`;
}

/// /cal?... URL — same params as /to, but the function returns text/calendar
/// so navigating to it opens the user's default calendar app directly
/// (iOS Calendar, Apple Calendar, Outlook, Google Calendar…) without
/// forcing a file download.
export function buildCalendarURL(c: Countdown, origin: string = location.origin): string {
  return `${origin}/cal?${buildCountdownParams(c)}`;
}

function buildCountdownParams(c: Countdown): string {
  const params = new URLSearchParams();
  params.set('t', c.title);
  params.set('e', encodeEmojiBase64Url(c.emoji));
  params.set('c', String(c.colorHex));
  params.set('d', linkDate(new Date(c.dueDate)));
  if (c.kind) {
    params.set('k', c.kind);
    if (c.originYear) params.set('oy', String(c.originYear));
  }
  params.set('fu', String(c.formatUnits));
  params.set('a', c.allDay ? '1' : '0');
  if (c.countOnlyWeekdays) params.set('w', '1');
  return params.toString();
}

/// The iOS websiteLinkFormat, "yyyy-MM-dd'T'HH:mmZ": local wall-clock plus
/// this device's offset at that date (+0100). The iOS parser requires the
/// offset; the server reads it too, to render the sender's wall-clock.
function linkDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const offset = -d.getTimezoneOffset();
  const sign = offset < 0 ? '-' : '+';
  const abs = Math.abs(offset);
  return `${dateToLocalISO(d)}${sign}${pad(Math.floor(abs / 60))}${pad(abs % 60)}`;
}

function encodeEmojiBase64Url(emoji: string): string {
  const bytes = new TextEncoder().encode(emoji);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
